import type { LegRole } from "@/lib/planner/route-objective";

type RouteRoleBadgeProps = {
  role: LegRole | undefined;
  score?: number;
  appeal?: number;
  appealSource?: "curated" | "heuristic";
  appealNote?: string | null;
};

export type RouteRoleChip = { label: string; title?: string };

const ROLE_LABELS: Record<LegRole, string> = {
  "primary-run": "Best run",
  "good-run": "Good run",
  connector: "Connector",
  "weak-necessary": "Link",
  uplift: "Lift",
  transfer: "Transfer",
  "return-home": "Back",
  "stop-detour": "Stop",
};

export function routeRoleLabel(role: LegRole | undefined): string | null {
  return role ? ROLE_LABELS[role] : null;
}

export function routeRoleChips({
  role,
  score,
  appeal,
  appealSource,
  appealNote,
}: RouteRoleBadgeProps): RouteRoleChip[] {
  const chips: RouteRoleChip[] = [];
  if (role === "connector") chips.push({ label: "Connector" });
  else if (role === "weak-necessary" || role === "return-home") {
    chips.push({ label: "Needed to get home" });
  } else if ((role === "primary-run" || role === "good-run") && score != null) {
    if (score >= 80) chips.push({ label: "Excellent" });
    else if (score >= 60) chips.push({ label: "Solid" });
  } else {
    const label = routeRoleLabel(role);
    if (label) chips.push({ label });
  }
  if (appealSource === "curated" && (appeal ?? 0) >= 1.15) {
    chips.push({
      label: "Local favourite",
      ...(appealNote ? { title: appealNote } : {}),
    });
  }
  return chips;
}

export function RouteRoleBadge(props: RouteRoleBadgeProps) {
  const chips = routeRoleChips(props);
  if (chips.length === 0) return null;

  return (
    <span className="contents">
      {chips.map((chip) => {
        const muted = chip.label === "Connector" || chip.label === "Transfer";
        const home = chip.label === "Needed to get home";
        const tone = home
          ? "border-navy/20 bg-navy/[0.06] text-navy"
          : muted
            ? "border-border-light bg-warm-bg text-text-muted"
            : "border-gold/25 bg-gold/[0.07] text-navy";
        return (
          <span
            key={chip.label}
            title={chip.title}
            className={`inline-flex h-[22px] shrink-0 items-center rounded-full border px-2 text-[10.5px] font-bold leading-none ${tone}`}
          >
            {chip.label}
          </span>
        );
      })}
    </span>
  );
}
